// Cutting a field. The cut can leave several separate pieces, and every one of them is kept.
import polygonClipping from 'polygon-clipping';

const toClip = (coords) => [coords.map((p) => [p.lng, p.lat])];
const fromClip = (ring) => ring.map(([lng, lat]) => ({ lat, lng }));

// Crumbs left when a cut line runs just past the field's edge, not real pieces of land.
const MIN_PIECE_HA = 0.005;

const piecesOf = (multiPolygon, calculateArea) => (multiPolygon || [])
  .filter((polygon) => polygon?.[0]?.length >= 3)
  .map((polygon) => {
    const coordinates = fromClip(polygon[0]);
    return { coordinates, area: calculateArea(coordinates) };
  })
  .filter((piece) => piece.area >= MIN_PIECE_HA)
  .sort((a, b) => b.area - a.area);

// Older years keep their own saved shape only on the first piece, so one year's map never shows it twice.
const yearDataWithoutShapes = (yearData) => Object.fromEntries(
  Object.entries(yearData || {}).map(([year, data]) => {
    const { coordinates, ...rest } = data || {};
    return [year, rest];
  }),
);

// Returns the new fields that replace the source, or null when the drawn shape cuts nothing.
export const splitField = (source, drawnCoords, selectedYear, calculateArea, now = Date.now()) => {
  const remaining = piecesOf(polygonClipping.difference(toClip(source.coordinates), toClip(drawnCoords)), calculateArea);
  if (remaining.length === 0) return null;
  const cut = piecesOf(polygonClipping.intersection(toClip(source.coordinates), toClip(drawnCoords)), calculateArea);
  if (cut.length === 0) return null;

  const origYd = source.yearData || {};
  const leftover = remaining.map((piece, index) => {
    const base = index === 0 ? origYd : yearDataWithoutShapes(origYd);
    return {
      ...source,
      id: now + index,
      name: `${source.name} (${index + 1})`,
      area: piece.area.toFixed(2),
      coordinates: piece.coordinates,
      yearData: { ...base, [selectedYear]: { ...(origYd[selectedYear] || {}), coordinates: piece.coordinates } },
    };
  });
  const removed = cut.map((piece, index) => ({
    id: now + remaining.length + index,
    name: `${source.name} (${remaining.length + index + 1})`,
    area: piece.area.toFixed(2),
    coordinates: piece.coordinates,
    tNumber: source.tNumber || '',
    comuna: source.comuna || '',
    company: source.company || '',
    apiaOnly: source.apiaOnly || false,
    folosinta: source.folosinta || 'arabil',
    yearData: {},
  }));
  return [...leftover, ...removed];
};
